import React, { useEffect, useRef, useState } from 'react'
import InputBase from 'design-system/input/base'
import { styled, TextFieldProps } from '@mui/material'
import { IMask } from 'react-imask'
import { formatMask } from 'utils/masks'

type CustomProps = {
  /** The mask to apply to the input */
  mask?: string | any
  /** Scale factor for the mask */
  scale?: number
  /** Whether the input accepts signed values */
  signed?: boolean
  /** Thousands separator for the mask */
  thousandsSeparator?: string
  /** Radix character for decimal values */
  radix?: string
  /** Whether to convert the input value to uppercase */
  toUpperCase?: boolean
  /** Callback function for change events */
  onChange?: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    value: any
  ) => void
}

/** InputMaskProps type extends CustomProps and TextFieldProps */
type InputMaskProps = CustomProps & Omit<TextFieldProps, 'onChange'>

/**
 * InputMask component
 * This component applies a mask to an input field, allowing for formatted input
 * such as numbers, dates, or custom formats.
 * It uses the IMask library for masking functionality.
 * @param {InputMaskProps} props - The properties for the input mask component.
 * @return {JSX.Element} The rendered input mask component.
 */
export const InputMask: React.FC<InputMaskProps> = ({
  value,
  onChange,
  onPaste,
  toUpperCase,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const maskRef = useRef<any>(null)
  const maskProps = props as CustomProps

  useEffect(() => {
    if (maskRef.current) {
      const maskedValue = formatMask(String(value), maskProps.mask)

      if (value != '' && value !== maskedValue && onChange) {
        onChange(
          {} as React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
          maskedValue
        )
      }
    }
  }, [value])

  useEffect(() => {
    if (inputRef.current) {
      maskRef.current = IMask(inputRef.current, maskProps)

      const input = inputRef.current

      input.addEventListener('input', () => {
        if (toUpperCase) input!.value = input!.value.toUpperCase()
      })

      maskRef.current.on('accept', () => {
        if (onChange) {
          onChange(
            {} as React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
            maskRef.current.value
          )
        }
      })
    }

    return () => {
      maskRef.current?.destroy()
    }
  }, [
    props.mask,
    props.scale,
    props.signed,
    props.thousandsSeparator,
    props.radix,
    onChange
  ])

  return (
    <InputBase
      {...props}
      inputRef={inputRef}
      value={value ?? ''}
      onPaste={() => {
        maskRef.current?.updateValue()
      }}
    />
  )
}

const StyledTextField = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.background.paper
  }
}))

export type NumberInputProps = {
  control?: [any, React.Dispatch<React.SetStateAction<any>>]
  mask?: string
} & TextFieldProps

const NumberInput: React.FC<NumberInputProps> = ({
  mask,
  control,
  ...props
}) => {
  const [innerValue, setInnerValue] = useState<string>('')
  const [value, setValue] = control ?? [innerValue, setInnerValue]

  const applyMask = (text: string) => {
    let formatted: string = text
    if (mask != null) {
      const regex = new RegExp(mask, 'g')
      const filtered = (text.match(regex) || []).join('')

      formatted = new RegExp(`^${mask}$`).test(filtered) ? filtered : ''
    }

    setValue(formatted)
  }

  return (
    <StyledTextField
      fullWidth
      {...props}
      value={value ?? ''}
      onChange={event => applyMask(event.target.value)}
    />
  )
}

export default NumberInput

export type TimeInputProps = {
  type?: 'hours' | 'minutes'
  onChange?: (value: string) => void
  value?: string
} & Omit<TextFieldProps, 'onChange' | 'value'>

export const TimeInput: React.FC<TimeInputProps> = ({
  type,
  onChange,
  value,
  ...props
}) => {
  const maxValue = type == 'hours' ? 24 : 60

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const char: string = (event.nativeEvent as any).data
    const val = event.target.value
    let result: string = ''
    if (!char) {
      result = val.padStart(2, '0')
    } else if (!Number.isNaN(val as any)) {
      const index = val.indexOf(value ?? '')
      const newValue = index
        ? index == 1
          ? `${char}${val[2]}`
          : val.substring(0, 2)
        : val.substring(1, 3)
      result = newValue.padStart(2, '0')
    }

    if (onChange)
      onChange(Number(result) > maxValue ? maxValue.toString() : result)
  }

  useEffect(() => {
    if (value == '') {
      if (onChange) onChange(value.padStart(2, '0'))
    }
  }, [])

  useEffect(() => {
    if (Number.isNaN(value as any)) {
      if (onChange)
        onChange((value ?? '').replaceAll(/[^\d]/g, '').padStart(2, '0'))
    }
  }, [value])

  return (
    <StyledTextField {...props} value={value ?? ''} onChange={handleChange} />
  )
}
